import ApiDatabase from 'server';
import { companiesPage, companiesQuery } from './crmCompanies';
import { forget, read, write } from './crmStore';
import crmPlanningApi from './crmPlanningApi';
import crmRecordApi from './crmRecordApi';
import crmGoalsApi from './crmGoalsApi';
import crmStatsApi from './crmStatsApi';
import { searchTaskTempWorkers } from './crmTempWorkers';
import { companyOf, homeView, personOf, taskOf, taskView, tasksView } from './crmViews';

/**
 * The api of the CRM pages: the home page, the tasks, a task's panel and form,
 * its comments and its activity. Each call has the `(payload, resolve,
 * reject)` shape of server/index.js.
 *
 * The default export goes to the real api (JBWEB-1405, JBWEB-1404) for the
 * tasks, the home page, the team, the contacts, the temp workers, the
 * callbacks and the list of the companies, and to the demo database
 * (crmMockData.js) for everything else:
 *
 *   getTasks            POST admin/crm/tasks               { people: [ids] | 'team' }    api
 *   countOpenTasks      POST admin/crm/tasks/count         { }                           api
 *   getTask             POST admin/crm/task                { id }                        api or demo, by id
 *   createTask          POST admin/crm/task/create         { fields }                    api
 *   updateTask          POST admin/crm/task/update         { id, fields }                api or demo, by id
 *   deleteTask          POST admin/crm/task/delete         { id }                        api or demo, by id
 *   restoreTask         POST admin/crm/task/restore        { id }                        api or demo, by id
 *   setTaskDone         POST admin/crm/task/done           { id, done }                  api or demo, by id
 *   assignTask          POST admin/crm/task/assign         { id, assigneeId }            api or demo, by id
 *   moveTask            POST admin/crm/task/due            { id, dueOn }                 api or demo, by id
 *   getTaskActivity     POST admin/crm/task/activity       { taskId }                    api or demo, by id
 *   getMentions         POST admin/crm/mentions            { page, pageSize, filter }    api
 *   getTeam             POST admin/crm/team                { }                           api
 *   getContacts         POST admin/crm/contacts            { }                           api
 *   getTaskTempWorkers  POST admin/crm/tempworkers         { search }                    api
 *   getHome             POST admin/crm/home                { }                           api
 *   setAppointmentDone  POST admin/crm/appointment/status  { id, done }                  api or demo, by id
 *   setCallbackDone     POST admin/crm/callback/done       { id, done }                  api
 *   listComments        POST admin/crm/task/comments       { taskId }                    api or demo, by id
 *   addComment          POST admin/crm/task/comment        { taskId, body, mentions }    api or demo, by id
 *   listAppointmentComments  POST admin/crm/appointment/comments  { id }                 api or demo, by id
 *   addAppointmentComment    POST admin/crm/appointment/comment   { id, body, mentions } api or demo, by id
 *   updateComment      POST admin/crm/task/comment/update   { taskId, commentId, body, mentions }  api or demo, by id (api: no taskId)
 *   deleteComment      POST admin/crm/task/comment/delete   { taskId, commentId }                 api or demo, by id (api: no taskId)
 *   updateAppointmentComment  POST admin/crm/appointment/comment/update  { id, commentId, body, mentions }  api or demo, by id
 *   deleteAppointmentComment  POST admin/crm/appointment/comment/delete  { id, commentId }                 api or demo, by id
 *   respondAppointment  POST admin/crm/appointment/respond { id, response }              api or demo, by id
 *   markTaskRead        POST admin/crm/task/read           { id }                        api or demo, by id
 *   listCompanies       POST admin/crm/companies           { tab, search, filters, page }  api
 *   assignCompany       POST admin/crm/company/assign      { companyId, role: 'commercial' | 'recruiter', personId }  api
 *   getCompanyCrm       POST admin/crm/company/crm         { companyId }                 api
 *   completeClient      POST admin/crm/company/client      { companyId, company }        api (JBWEB-1422)
 *                       a prospect becomes a client: `company` is the body of company/add,
 *                       written on the prospect's own company (no second company)
 *   moveCompanyToOld    POST admin/crm/company/old         { companyId, recruiterId }    api (JBWEB-1423)
 *                       a new client becomes an old one, followed by its recruiter (520,
 *                       else 500) with its commercial
 *   moveCompanyToNew    POST admin/crm/company/new         { companyId }                 api (JBWEB-1423)
 *                       an old client back to the new ones: oldClientSince unset, its
 *                       recruiter kept; 409 NOT_AN_OLD_CLIENT
 *   markCompanyLost     POST admin/crm/company/lost        { companyId, lostReason, lostDetails?, relaunchOn | null, commercialId? }  api (JBWEB-1423)
 *                       lostReason: price | competitor | noNeed | direct | unreachable | closed;
 *                       a relaunch goes to the company's commercial, so commercialId is
 *                       asked for when it has none (400 COMMERCIAL_REQUIRED)
 *   reactivateProspect  POST admin/crm/prospect/reactivate { companyId }                 api
 *                       a lost company back to prospect, its pending relaunch canceled
 *   The moves of a stage are the agency manager's and the company's commercial's
 *   (helper/crmStageRights.js; 403 STAGE_FORBIDDEN, PROSPECT_FORBIDDEN).
 *   saveCompanyContact  POST admin/crm/company/contact/save   { companyId, contact }     api (JBWEB-1424)
 *   deleteCompanyContact POST admin/crm/company/contact/delete { companyId, contactId }  api (JBWEB-1424)
 *   linkCompanyContact  POST admin/crm/company/contact/link   { companyId, contactId }   api (JBWEB-1424)
 *                       a contact of the company without an account: { id, firstname,
 *                       lastname, phone, email, position, userId }; getCompanyCrm lists them
 *                       as `contacts`. save answers { contact } (no id adds it), delete
 *                       { contactId }, link { contact } once its invitation made an account
 *                       of its e-mail (`userId`). 400 INVALID_ID, INVALID_CONTACT (an empty
 *                       position is one); 403 COMPANY_CONTACT_FORBIDDEN (500 and 510 only);
 *                       404 COMPANY_NOT_FOUND, COMPANY_CONTACT_NOT_FOUND; 409
 *                       COMPANY_CONTACTS_FULL, CONTACT_NOT_INVITED
 *   setCompanyEmail     POST admin/crm/company/email       { companyId, email | null }   api (JBWEB-1425)
 *                       the company's general address, answered { companyId, email } and read
 *                       back as `crm.email` of getCompanyCrm; 400 INVALID_ID, INVALID_EMAIL;
 *                       403 COMPANY_CONTACT_FORBIDDEN (500 and 510 only); 404 COMPANY_NOT_FOUND
 *   setContactPosition  POST admin/crm/contact/position    { email, position }           api (JBWEB-1425)
 *                       a person's position, wherever their e-mail is (their account's
 *                       `user.position`, the contacts without an account); getCompanyCrm
 *                       answers those of the members as `memberPositions`. 400 INVALID_EMAIL,
 *                       INVALID_CONTACT; 403 COMPANY_CONTACT_FORBIDDEN
 *   getGroupRecord      POST admin/crm/group               { groupId }                   api (crmRecordApi.js)
 *   getGroupActivity    POST admin/crm/group/activity      { groupId }                   api (read by getGroupRecord)
 *   saveGroupContact    POST admin/crm/group/contact/save  { groupId, contact }          api
 *   deleteGroupContact  POST admin/crm/group/contact/delete { groupId, contactId }       api
 *   removeCompanyFromGroup  POST admin/crm/group/company/remove  { groupId, companyId }  api
 *   addRecordNote       POST admin/crm/note/create         { body, links, callId?, phoneCallId? }  api
 *   updateRecordNote    POST admin/crm/note/update         { id, body, links }           api
 *   deleteRecordNote    POST admin/crm/note/delete         { id }                        api
 *   listRecordNotes     POST admin/crm/notes               { companyId | groupId | contactId }  api
 *   sendRecordEmail     POST admin/crm/record/email/send   { companyId | groupId, contactName, to, cc, bcc, subject, body }  api
 *   listRecordEmails    POST admin/crm/record/emails       { companyId } | { groupId }   api (read by the record pages)
 *
 * sendRecordEmail answers { email }; listRecordEmails { data: [email],
 * intercom: 'ok' | 'unavailable' } (the received ones come from Intercom; when
 * it does not answer, the sent ones still come). An e-mail is { id, direction:
 * 'sent' | 'received', at, authorId, contactName, address, to, cc, subject,
 * body, source: 'jobbiz' | 'intercom', conversationId? }. Refusals: 400
 * INVALID_EMAIL_FIELD, SCHEDULING_UNAVAILABLE (no programmed sending), and
 * EMAIL_SENDING_DISABLED, EMAIL_SEND_FAILED of the sending service.
 *
 *   getRecordCalls      POST admin/crm/record/calls        { companyId | groupId }       api
 *                       the Wildix calls of the record's numbers → { data: [{ id, at, direction,
 *                       missed, talkSeconds, ringSeconds, userId, remotePhone, contactName }] }
 *
 * A group's contact is { id, firstname, lastname, email, phone, position };
 * with `contact.id`, saveGroupContact edits it, and it answers { contact }.
 * deleteGroupContact answers { id }. Only a Responsable d'agence or a
 * Commercial writes them (403 GROUP_CONTACT_FORBIDDEN); 404 GROUP_NOT_FOUND,
 * GROUP_CONTACT_NOT_FOUND; 400 INVALID_CONTACT (a first name or a last name,
 * a valid e-mail); 409 GROUP_CONTACTS_FULL past 50. removeCompanyFromGroup,
 * open to every CRM role, answers { groupId, companyId }: 404
 * GROUP_NOT_FOUND, 409 COMPANY_NOT_IN_GROUP.
 *
 * getGroupActivity answers { tasks, appointments, calls, notes } of the
 * group and of its companies, the latest first, 200 at most of each: tasks
 * as getTasks lists them; appointments { id, title, category, status,
 * startsAt, endsAt, allDay, ownerId, owner, companyId, groupId } (neither
 * canceled nor someone else's private ones); calls { id, at, result, note,
 * contactName, companyId, groupId, author }; notes as below. 404
 * GROUP_NOT_FOUND.
 *
 * A note's `links` are those of a task, without temp workers (1 to 20,
 * [{ type: 'company' | 'group' | 'contact', id, companyId? }]); its body is 1
 * to 5,000 characters. addRecordNote answers { note } (with `callId`, the
 * call's company or group is linked if missing), updateRecordNote { note },
 * deleteRecordNote { id }, listRecordNotes { notes } (the notes linked to that
 * very record, the latest first, 200 at most; contactId: the account's id or
 * 'name:<name>'). A note is { id, body, links: [{ type, id, label, sub,
 * companyId }], callId, authorId, author, createdAt, editedAt, canEdit (the
 * author), canDelete (the author, or a Responsable d'agence) }. Refusals: 400
 * NOTE_BODY_REQUIRED, NOTE_TOO_LONG, INVALID_LINKS, INVALID_CALL; 403
 * NOTE_EDIT_FORBIDDEN, NOTE_DELETE_FORBIDDEN; 404 NOTE_NOT_FOUND.
 *
 * The `doneAgenda` of getTasks is real: the appointments done and the
 * callbacks done of the people of the page, the latest first, each
 * { kind: 'appointment' | 'callback', id, title, doneAt, dueOn,
 * company: { id, name } | null, contactName, access: { canEdit } }
 * (a callback's title is null: the screen draws it from tasks.callbackTitle).
 *
 * A task's `links` (getTasks, getHome, getTask, every detailed answer) are
 * [{ type: 'company' | 'group' | 'contact' | 'tempWorker', id, label, sub?,
 * companyId?, validated? }]; a record gone since keeps its link with label ''.
 * createTask and updateTask send `fields.links` as [{ type, id, companyId? }]
 * (20 at most): a contact with a record is { type: 'contact', id: userId,
 * companyId }, the userId of its getContacts option; a contact without one is
 * { type: 'contact', id: 'name:<name>', companyId? }. With `links`, the api
 * draws companyId, groupId and contactName from them; a link that does not
 * exist or does not hold answers 400 INVALID_LINKS.
 *
 * getTaskTempWorkers answers [{ id, firstname, lastname, city, validated }],
 * 20 at most, whose first name or last name starts with `search` (2 to 50
 * characters, 400 INVALID_SEARCH otherwise).
 *
 * setCallbackDone ticks or unticks a callback of doneAgenda and answers
 * { id, done }: 404 CALLBACK_NOT_FOUND, 403 CALLBACK_FORBIDDEN (not the
 * caller's), 409 CALLBACK_PENDING_EXISTS (reopening while another callback of
 * the company waits).
 *
 * A comment view (list, add, update) is { id, authorId, author, body, mentions,
 * createdAt, editedAt (ISO string or null), canEdit (the viewer is the author),
 * canDelete (the author, or a Responsable d'agence) }. Deleting answers { id }.
 * A missing comment rejects with errorCode COMMENT_NOT_FOUND.
 *
 * getHome answers the shape of `homeView` (crmViews.js). Its `appointments`
 * come from the api: the caller's of the day (Martinique), not canceled, by
 * startsAt, in the shape of the planning's. `appointmentsToPlace` counts
 * the caller's visits to place (zeros as long as the api does not count them).
 * `calls` is filled for a role that prospects (null otherwise); its
 * `toQualify` is 0 until the telephony is wired.
 *
 * "By id": the planning and the record pages still show the demo's tasks
 * (ids `t…`), which the api does not know. A call on a task goes to the api
 * when its id is a real one (an ObjectId), to the demo otherwise. This switch
 * goes away once those pages read the real tasks, with their own backs.
 * The appointments follow the same switch: a call on one goes to the api for
 * an ObjectId only, and the demo keeps the comments of its own appointments
 * under taskId `a:<id>`.
 * `crmMockApi` is the whole demo, for those pages and for the tests of the
 * screens.
 *
 * listCompanies sends `tab` (prospects | new | old | lost), `search` (100
 * characters at most, no fuzzy match) and `filters` (companiesQuery,
 * crmCompanies.js: departments, filterCategory, supplierSearch,
 * missionCountMode, missionCountMin, incompleteRemuneration, sizes
 * ['grand_compte' | 'pme' | 'tpe'], then action 'all' | 'with' | 'without'
 * and commercial 'all' | 'none' | id); a value the
 * api does not know answers 400 INVALID_FILTERS. The api answers { data,
 * page, totalPages, counts: { prospects, new, old, lost } }, 20 rows a page,
 * which the page reads as { companies, counts, count, page, pages, perPage }
 * (companiesPage). A row holds the company (name_commercial, reason_social,
 * siret, department, pennylane_category_id, createdAt, missionCount,
 * bigAccounts, group { id, name } | null, size 'grand_compte' | 'pme' | 'tpe' |
 * null), its CRM state (stage, clientSince,
 * lostAt, lostReason, relaunchOn, commercialId, recruiterId) and the
 * signed-in user's own next action: { kind: 'task', id, title, dueOn,
 * dueTime, at } | { kind: 'appointment', id, category, title, at } |
 * { kind: 'callback', id, at } | null.
 *
 * assignCompany answers { companyId, commercialId, recruiterId }: 400
 * INVALID_ASSIGN_ROLE, 400 INVALID_ASSIGNEE (not an active 510 for a
 * commercial, a 520 or a 500 for a recruiter), 409 ASSIGN_NOT_CLIENT (a
 * recruiter for a company that is not a client), 403 ASSIGN_FORBIDDEN, 404
 * COMPANY_NOT_FOUND.
 *
 * The record pages (a company, a group, a contact), the planning and the
 * statistics have their own calls, listed in crmRecordApi.js,
 * crmPlanningApi.js and crmStatsApi.js, the goals of the agency in
 * crmGoalsApi.js.
 *
 * `scenario` (the page's `?mock=`) and `me` (the signed-in user) only serve
 * the demo: the api knows who asks, so they are never sent to it.
 */

const TYPE_TITLES = { todo: 'À faire', call: 'Appel', email: 'E-mail', visit: 'Visite', meeting: 'Réunion' };
// The demo's stand-in for the title the api will draw from the description.
const titleFrom = (note) => (note.split(/[.\n]/)[0] || '').trim().slice(0, 70);
// The fields a change of which the activity of a task records.
const TRACKED = [
  'title', 'type', 'note', 'dueOn', 'dueTime', 'durationMin', 'assigneeId', 'companyId', 'contactName',
  'emailTemplate', 'emailSubject', 'emailBody',
];

// The demo's stand-in for the e-mail the api will draft from the description.
const draftEmail = (db, fields) => {
  const found = companyOf(db, fields.companyId);
  const words = `${fields.note} ${fields.title ?? ''}`.toLowerCase();
  const template = words.includes('devis') ? 'Devis' : words.includes('relan') ? 'Relance de proposition' : 'Envoi de la plaquette';
  const contact = fields.contactName || found?.contactName || '';
  return {
    emailTemplate: template,
    emailSubject: `${template} · ${found?.name ?? 'Jobbiz'}`,
    emailBody: `Bonjour${contact ? ` ${contact.split(' ')[0]}` : ''},\n\n${fields.note.trim() || 'Je reviens vers vous.'}\n\nBelle journée,`,
  };
};

const record = (db, taskId, kind, actorId, changes = {}) => {
  db.activity[taskId] = [
    ...(db.activity[taskId] ?? []),
    { id: `e${Date.now()}${Math.random().toString(16).slice(2, 6)}`, kind, actorId, at: new Date().toISOString(), changes },
  ];
};

const found = (db, id) => {
  const item = taskOf(db, id);
  if (!item) throw new Error('Cette tâche n’existe plus.');
  return item;
};

const commentView = (db, comment, me) => ({
  ...comment,
  editedAt: comment.editedAt ?? null,
  author: personOf(db, comment.authorId),
  canEdit: comment.authorId === me.id,
  canDelete: comment.authorId === me.id,
});

// Rejects like the api does, so crmTaskErrorText can translate the code.
const commentFound = (db, taskId, commentId) => {
  const comment = (db.comments[taskId] ?? []).find((other) => other.id === commentId);
  if (!comment) {
    throw Object.assign(new Error('Ce commentaire n’existe plus.'), {
      response: { status: 404, data: { error: 'Comment not found', errorCode: 'COMMENT_NOT_FOUND' } },
    });
  }
  return comment;
};

const crmApi = {
  getHome: (payload, resolve, reject) => read(payload, resolve, reject, homeView),

  getTasks: (payload, resolve, reject) =>
    read(payload, resolve, reject, (db, me) => tasksView(db, me, payload?.people)),

  countOpenTasks: (payload, resolve, reject) =>
    read(payload, resolve, reject, (db, me) => ({
      count: db.tasks.filter((item) => item.assigneeId === me.id && !item.doneAt).length,
    })),

  getTask: (payload, resolve, reject) =>
    read(payload, resolve, reject, (db) => taskView(db, found(db, payload.id))),

  getTeam: (payload, resolve, reject) => read(payload, resolve, reject, (db) => db.team),

  // The temp workers whose first name or last name starts with `search`:
  // [{ id, firstname, lastname, city, validated }]; none under 2 letters, as the api.
  getTaskTempWorkers: (payload, resolve, reject) =>
    read(payload, resolve, reject, () => searchTaskTempWorkers(payload?.search)),

  // An option per company and per contact of a company, as the form lists them.
  getContacts: (payload, resolve, reject) =>
    read(payload, resolve, reject, (db) =>
      db.companies.flatMap((item) => [
        { key: `c-${item.id}`, companyId: item.id, label: item.name, company: item.name, contact: '', phone: item.phone, email: item.email, stage: item.stage },
        ...(item.contactName
          ? [{ key: `p-${item.id}`, companyId: item.id, label: `${item.contactName} · ${item.name}`, company: item.name, contact: item.contactName, role: item.contactRole, phone: item.phone, email: item.email, stage: item.stage }]
          : []),
      ]),
    ),

  createTask: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db, me) => {
      const fields = payload.fields;
      const item = {
        id: `t${Date.now()}`,
        type: fields.type,
        title: fields.title?.trim() || titleFrom(fields.note) || TYPE_TITLES[fields.type],
        note: fields.note.trim(),
        dueOn: fields.dueOn,
        dueTime: fields.dueTime ?? null,
        durationMin: fields.durationMin,
        doneAt: null,
        contactName: fields.contactName ?? '',
        contactRole: fields.contactRole ?? '',
        contactDetail: fields.contactDetail ?? '',
        companyId: fields.companyId ?? null,
        groupId: fields.groupId ?? null,
        // Every record of the task; the fields above hold the first of each kind.
        links: fields.links ?? [],
        assigneeId: fields.assigneeId || me.id,
        callbackId: null,
        appointmentId: null,
        emailTemplate: '',
        emailSubject: '',
        emailBody: '',
        unread: false,
        createdAt: new Date().toISOString(),
        createdBy: me.id,
        ...(fields.type === 'email' ? draftEmail(db, fields) : {}),
      };
      db.tasks.push(item);
      record(db, item.id, 'created', me.id);
      return taskView(db, item);
    }),

  updateTask: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db, me) => {
      const item = found(db, payload.id);
      const changes = {};
      TRACKED.forEach((field) => {
        if (field in payload.fields && (payload.fields[field] ?? null) !== (item[field] ?? null)) {
          changes[field] = [item[field] ?? null, payload.fields[field] ?? null];
        }
      });
      Object.assign(item, payload.fields);
      if (Object.keys(changes).length) record(db, item.id, 'updated', me.id, changes);
      return taskView(db, item);
    }),

  deleteTask: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const item = found(db, payload.id);
      db.tasks = db.tasks.filter((other) => other.id !== item.id);
      return item;
    }),

  restoreTask: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      if (!taskOf(db, payload.task.id)) db.tasks.push(payload.task);
      return payload.task;
    }),

  setTaskDone: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db, me) => {
      const item = found(db, payload.id);
      item.doneAt = payload.done ? new Date().toISOString() : null;
      record(db, item.id, payload.done ? 'done' : 'reopened', me.id);
      return { id: item.id, done: payload.done };
    }),

  assignTask: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db, me) => {
      const item = found(db, payload.id);
      record(db, item.id, 'updated', me.id, { assigneeId: [item.assigneeId, payload.assigneeId] });
      item.assigneeId = payload.assigneeId;
      return { id: item.id, assigneeId: item.assigneeId };
    }),

  moveTask: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db, me) => {
      const item = found(db, payload.id);
      record(db, item.id, item.doneAt ? 'reopened' : 'updated', me.id, { dueOn: [item.dueOn, payload.dueOn] });
      item.dueOn = payload.dueOn;
      item.doneAt = null;
      return { id: item.id, dueOn: item.dueOn };
    }),

  setAppointmentDone: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const item = db.appointments.find((other) => other.id === payload.id);
      if (item) item.status = payload.done ? 'done' : 'planned';
      return { id: payload.id, done: payload.done };
    }),

  setCallbackDone: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const item = db.callbacks.find((other) => other.id === payload.id);
      if (item) item.doneAt = payload.done ? new Date().toISOString() : null;
      return { id: payload.id, done: payload.done };
    }),

  listComments: (payload, resolve, reject) =>
    read(payload, resolve, reject, (db, me) =>
      (db.comments[payload.taskId] ?? []).map((comment) => commentView(db, comment, me)),
    ),

  addComment: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db, me) => {
      const comment = {
        id: `m${Date.now()}`,
        authorId: me.id,
        body: payload.body,
        mentions: payload.mentions ?? [],
        createdAt: new Date().toISOString(),
        editedAt: null,
      };
      db.comments[payload.taskId] = [...(db.comments[payload.taskId] ?? []), comment];
      return commentView(db, comment, me);
    }),

  // The demo's author is the only one who edits or deletes (no agency head).
  updateComment: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db, me) => {
      const comment = commentFound(db, payload.taskId, payload.commentId);
      comment.body = payload.body;
      comment.mentions = payload.mentions ?? [];
      comment.editedAt = new Date().toISOString();
      return commentView(db, comment, me);
    }),

  deleteComment: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const comment = commentFound(db, payload.taskId, payload.commentId);
      db.comments[payload.taskId] = db.comments[payload.taskId].filter((other) => other !== comment);
      return { id: comment.id };
    }),

  // The demo keeps the comments of an appointment with those of the tasks.
  listAppointmentComments: (payload, resolve, reject) =>
    crmApi.listComments({ ...payload, taskId: `a:${payload.id}` }, resolve, reject),

  addAppointmentComment: (payload, resolve, reject) =>
    crmApi.addComment({ ...payload, taskId: `a:${payload.id}` }, resolve, reject),

  updateAppointmentComment: (payload, resolve, reject) =>
    crmApi.updateComment({ ...payload, taskId: `a:${payload.id}` }, resolve, reject),

  deleteAppointmentComment: (payload, resolve, reject) =>
    crmApi.deleteComment({ ...payload, taskId: `a:${payload.id}` }, resolve, reject),

  // The history of a task, oldest first; a demo task created before the
  // history starts with its creation.
  getTaskActivity: (payload, resolve, reject) =>
    read(payload, resolve, reject, (db) => {
      const item = found(db, payload.taskId);
      const events = db.activity[item.id] ?? [];
      const created = events.some((event) => event.kind === 'created')
        ? []
        : [{ id: `${item.id}-created`, kind: 'created', actorId: item.createdBy, at: item.createdAt, changes: {} }];
      const done =
        item.doneAt && !events.some((event) => event.kind === 'done')
          ? [{ id: `${item.id}-done`, kind: 'done', actorId: item.assigneeId, at: item.doneAt, changes: {} }]
          : [];
      return [...created, ...events, ...done].map((event) => ({ ...event, actor: personOf(db, event.actorId) }));
    }),

  markTaskRead: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const item = taskOf(db, payload.id);
      if (item) item.unread = false;
      return { id: payload.id };
    }),
};

// The record pages (a company, a group, a contact) answer from
// crmRecordApi.js, the planning from crmPlanningApi.js, the statistics from
// crmStatsApi.js.
export const crmMockApi = { ...crmApi, ...crmRecordApi, ...crmPlanningApi, ...crmStatsApi, ...crmGoalsApi };

const isRealId = (id) => typeof id === 'string' && /^[0-9a-f]{24}$/i.test(id);

// The keys of the contract, out of a page's payload (never `scenario` or `me`).
const pick =
  (...keys) =>
  (payload) =>
    Object.fromEntries(keys.map((key) => [key, payload[key]]));

// Read at call time, so that a test's mock of server/index.js is the one called.
const toApi = (method, bodyOf) => (payload, resolve, reject) =>
  ApiDatabase[method](bodyOf(payload ?? {}), resolve, reject);

const byId =
  (name, method, bodyOf, idOf = (payload) => payload.id) =>
  (payload, resolve, reject) =>
    isRealId(idOf(payload ?? {}))
      ? toApi(method, bodyOf)(payload, resolve, reject)
      : crmMockApi[name](payload, resolve, reject);

// The undo of a deletion hands back the deleted task, the api wants its id.
const restoredId = (payload) => payload.id ?? payload.task?.id;

const allCrmApi = {
  ...crmMockApi,
  getTasks: toApi('getCrmTasks', pick('people')),
  countOpenTasks: toApi('countCrmOpenTasks', pick()),
  createTask: toApi('createCrmTask', pick('fields')),
  getHome: toApi('getCrmHome', pick()),
  // One page of the comments that name the signed-in user, the latest first:
  // { data: [{ id, body, createdAt, author: { id, firstname, lastname }, task: { id, title }, unread }],
  //   page, pageSize, total, totalPages, counts: { all, unread } }
  // `unread`: the comment is newer than the last time its reader opened the task.
  getMentions: toApi('getCrmMentions', pick('page', 'pageSize', 'filter')),
  getTeam: toApi('getCrmTeam', pick()),
  listCompanies: (payload, resolve, reject) =>
    ApiDatabase.getCrmCompanies(
      companiesQuery(payload ?? {}),
      (answer) => resolve(companiesPage(answer, payload?.tab ?? 'prospects')),
      reject,
    ),
  assignCompany: toApi('assignCrmCompany', pick('companyId', 'role', 'personId')),
  // { crm: { stage, clientSince, oldClientSince, lostAt, lostReason, lostDetails, relaunchOn, commercialId, recruiterId, email },
  //   nextAction: { kind: 'task', id, title, dueOn, dueTime, at } | { kind: 'appointment', id, category, title, at }
  //     | { kind: 'callback', id, at } | null,
  //   contacts: [{ id, firstname, lastname, phone, email, position, userId }],
  //   memberPositions: { [userId]: position } }; 400 INVALID_ID, 404 COMPANY_NOT_FOUND
  getCompanyCrm: toApi('getCrmCompanyCrm', pick('companyId')),
  saveCompanyContact: toApi('saveCrmCompanyContact', pick('companyId', 'contact')),
  deleteCompanyContact: toApi('deleteCrmCompanyContact', pick('companyId', 'contactId')),
  linkCompanyContact: toApi('linkCrmCompanyContact', pick('companyId', 'contactId')),
  // '' (the field emptied) unsets it, sent as null.
  setCompanyEmail: toApi('setCrmCompanyEmail', ({ companyId, email }) => ({ companyId, email: email || null })),
  setContactPosition: toApi('setCrmContactPosition', pick('email', 'position')),
  completeClient: toApi('completeCrmClient', pick('companyId', 'company')),
  moveCompanyToOld: toApi('moveCrmCompanyToOld', pick('companyId', 'recruiterId')),
  moveCompanyToNew: toApi('moveCrmCompanyToNew', pick('companyId')),
  // lostDetails and commercialId only when given: the api keeps them optional.
  markCompanyLost: toApi('markCrmCompanyLost', ({ companyId, lostReason, lostDetails, relaunchOn, commercialId }) => ({
    companyId,
    lostReason,
    ...(lostDetails ? { lostDetails } : {}),
    relaunchOn: relaunchOn ?? null,
    ...(commercialId ? { commercialId } : {}),
  })),
  reactivateProspect: toApi('reactivateCrmProspect', pick('companyId')),
  getContacts: toApi('getCrmContacts', pick()),
  getTaskTempWorkers: toApi('searchCrmTaskTempWorkers', pick('search')),
  getTask: byId('getTask', 'getCrmTask', pick('id')),
  updateTask: byId('updateTask', 'updateCrmTask', pick('id', 'fields')),
  deleteTask: byId('deleteTask', 'deleteCrmTask', pick('id')),
  restoreTask: byId(
    'restoreTask',
    'restoreCrmTask',
    (payload) => ({ id: restoredId(payload) }),
    restoredId,
  ),
  setTaskDone: byId('setTaskDone', 'setCrmTaskDone', pick('id', 'done')),
  assignTask: byId('assignTask', 'assignCrmTask', pick('id', 'assigneeId')),
  moveTask: byId('moveTask', 'moveCrmTask', pick('id', 'dueOn')),
  getTaskActivity: byId(
    'getTaskActivity',
    'getCrmTaskActivity',
    pick('taskId'),
    (payload) => payload.taskId,
  ),
  listComments: byId(
    'listComments',
    'getCrmTaskComments',
    pick('taskId'),
    (payload) => payload.taskId,
  ),
  addComment: byId(
    'addComment',
    'addCrmTaskComment',
    pick('taskId', 'body', 'mentions'),
    (payload) => payload.taskId,
  ),
  updateComment: byId(
    'updateComment',
    'updateCrmTaskComment',
    pick('commentId', 'body', 'mentions'),
    (payload) => payload.taskId,
  ),
  deleteComment: byId(
    'deleteComment',
    'deleteCrmTaskComment',
    pick('commentId'),
    (payload) => payload.taskId,
  ),
  markTaskRead: byId('markTaskRead', 'markCrmTaskRead', pick('id')),
  getPlanning: toApi('getCrmPlanning', pick('from', 'to', 'ownerId')),
  // groupId (a group's record), beside companyId: an existing group, else
  // 400 UNKNOWN_GROUP; never sent to Google.
  createAppointment: toApi(
    'createCrmAppointment',
    pick(
      'ownerId',
      'category',
      'title',
      'description',
      'allDay',
      'startDay',
      'endDay',
      'startsAt',
      'endsAt',
      'companyId',
      'groupId',
      'location',
      'mode',
      'contactName',
      'attendees',
      'meet',
      'reminders',
      'colorId',
      'transparency',
      'visibility',
      'recurrence',
    ),
  ),
  getAppointment: byId('getAppointment', 'getCrmAppointment', pick('id')),
  updateAppointment: byId(
    'updateAppointment',
    'updateCrmAppointment',
    pick('id', 'fields', 'scope'),
  ),
  setAppointmentDone: byId(
    'setAppointmentDone',
    'setCrmAppointmentDone',
    pick('id', 'done'),
  ),
  // A note on a call: `callId` a call logged in the CRM, `phoneCallId` a Wildix call.
  addRecordNote: toApi('createCrmNote', ({ body, links, callId, phoneCallId }) => ({
    body,
    links,
    ...(callId ? { callId } : {}),
    ...(phoneCallId ? { phoneCallId } : {}),
  })),
  updateRecordNote: toApi('updateCrmNote', pick('id', 'body', 'links')),
  deleteRecordNote: toApi('deleteCrmNote', pick('id')),
  // Exactly one record: the first of companyId, groupId, contactId given.
  listRecordNotes: toApi('getCrmNotes', (payload) => {
    const key = ['companyId', 'groupId', 'contactId'].find((one) => payload[one]);
    return key ? { [key]: payload[key] } : {};
  }),
  getGroupActivity: toApi('getCrmGroupActivity', pick('groupId')),
  // Exactly one record: the group when there is one, else the company.
  sendRecordEmail: toApi('sendCrmRecordEmail', ({ companyId, groupId, contactName, to, cc, bcc, subject, body }) => ({
    ...(groupId ? { groupId } : { companyId }),
    contactName,
    to,
    cc,
    bcc,
    subject,
    body,
  })),
  listRecordEmails: toApi('getCrmRecordEmails', ({ companyId, groupId }) => (groupId ? { groupId } : { companyId })),
  // The Wildix calls of one record, matched by its phone numbers: the first of companyId, groupId given.
  getRecordCalls: toApi('getCrmRecordCalls', (payload) => {
    const key = ['companyId', 'groupId'].find((one) => payload[one]);
    return key ? { [key]: payload[key] } : {};
  }),
  saveGroupContact: toApi('saveCrmGroupContact', pick('groupId', 'contact')),
  deleteGroupContact: toApi('deleteCrmGroupContact', pick('groupId', 'contactId')),
  // The groups kept a moment (crmStore.js) are read again after it.
  removeCompanyFromGroup: (payload, resolve, reject) =>
    toApi('removeCrmGroupCompany', pick('groupId', 'companyId'))(
      payload,
      (data) => {
        forget('groups');
        resolve(data);
      },
      reject,
    ),
  // The callbacks of the list come from the api only (doneAgenda), so no demo id to route.
  setCallbackDone: toApi('setCrmCallbackDone', pick('id', 'done')),
  removeAppointment: byId(
    'removeAppointment',
    'deleteCrmAppointment',
    pick('id', 'scope'),
  ),
  listAppointmentComments: byId(
    'listAppointmentComments',
    'listCrmAppointmentComments',
    pick('id'),
  ),
  addAppointmentComment: byId(
    'addAppointmentComment',
    'addCrmAppointmentComment',
    pick('id', 'body', 'mentions'),
  ),
  updateAppointmentComment: byId(
    'updateAppointmentComment',
    'updateCrmAppointmentComment',
    pick('commentId', 'body', 'mentions'),
  ),
  deleteAppointmentComment: byId(
    'deleteAppointmentComment',
    'deleteCrmAppointmentComment',
    pick('commentId'),
  ),
  // An invitee's answer: response is 'accepted' | 'declined' | 'tentative'.
  respondAppointment: byId(
    'respondAppointment',
    'respondCrmAppointment',
    pick('id', 'response'),
  ),
};

export default allCrmApi;
